// Shrinks an image in the browser before it is uploaded: a square of at most `size` px (the workspace logo's
// centre, or the part of a profile photo chosen in ProfilePhoto's crop dialog), encoded as WebP where the
// browser can (else PNG when it has transparency, JPEG otherwise). A typical photo comes out around 10–40 KB
// instead of several MB.

const MAX_INPUT = 25 * 1024 * 1024;

/** Checks a picked file is a readable image of reasonable size, and decodes it. */
export async function readImage(file: File): Promise<HTMLImageElement> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.');
  if (file.size > MAX_INPUT) throw new Error('That image is too large (25 MB max).');
  return loadImage(file);
}

function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  return img.decode().then(
    () => (URL.revokeObjectURL(url), img),
    () => {
      URL.revokeObjectURL(url);
      throw new Error("This image couldn't be read. Try a PNG, JPG or WebP file.");
    },
  );
}

function canvasOf(side: number) {
  const c = document.createElement('canvas');
  c.width = c.height = side;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return { c, ctx };
}

const toBlob = (c: HTMLCanvasElement, type: string, quality?: number) => new Promise<Blob | null>((resolve) => c.toBlob(resolve, type, quality));

function hasTransparency(ctx: CanvasRenderingContext2D, side: number) {
  const px = ctx.getImageData(0, 0, side, side).data;
  for (let i = 3; i < px.length; i += 4) if (px[i] < 255) return true;
  return false;
}

export async function compressSquareImage(file: File, size = 256, quality = 0.85): Promise<File> {
  const img = await readImage(file);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const side = Math.min(w, h);
  if (!side) throw new Error("This image couldn't be read.");
  return encodeSquare(img, (w - side) / 2, (h - side) / 2, side, size, quality, 'logo');
}

/** The square (sx, sy, side) of an image, in its own pixels, as a small file of at most `size` px. */
export function cropSquareImage(img: HTMLImageElement, sx: number, sy: number, side: number, size = 512, quality = 0.88): Promise<File> {
  return encodeSquare(img, sx, sy, side, size, quality, 'photo');
}

async function encodeSquare(img: HTMLImageElement, sx: number, sy: number, side: number, size: number, quality: number, name: string): Promise<File> {
  // crop the square (bounded to keep memory sane), then halve repeatedly for a sharp result
  let cur = Math.round(Math.min(side, 4096));
  let step = canvasOf(cur);
  step.ctx.drawImage(img, sx, sy, side, side, 0, 0, cur, cur);
  const out = Math.min(size, cur);
  while (cur / 2 >= out) {
    const next = canvasOf(Math.round(cur / 2));
    next.ctx.drawImage(step.c, 0, 0, cur, cur, 0, 0, next.c.width, next.c.width);
    step = next;
    cur = next.c.width;
  }
  const final = canvasOf(out);
  final.ctx.drawImage(step.c, 0, 0, cur, cur, 0, 0, out, out);

  const webp = await toBlob(final.c, 'image/webp', quality);
  if (webp && webp.type === 'image/webp') return new File([webp], `${name}.webp`, { type: 'image/webp' });
  const png = hasTransparency(final.ctx, out);
  const blob = png ? await toBlob(final.c, 'image/png') : await toBlob(final.c, 'image/jpeg', quality);
  if (!blob) throw new Error("This image couldn't be compressed.");
  return new File([blob], png ? `${name}.png` : `${name}.jpg`, { type: blob.type });
}
